export type QdsIconLibrary = 'prime' | 'lucide';

export interface QdsIconReference {
  library: QdsIconLibrary;
  name: string;
}

type LucideRuntime = {
  createElement?: (icon: unknown) => SVGElement;
  [name: string]: unknown;
};

let lucideRuntimePromise: Promise<LucideRuntime | null> | null = null;

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function toKebabCase(value: string): string {
  return value.replace(/([A-Z])/g, (_match, letter, index) => index === 0 ? letter.toLowerCase() : `-${letter.toLowerCase()}`);
}

function getLucideRuntime(): LucideRuntime | null {
  const candidate = (globalThis as typeof globalThis & { lucide?: unknown }).lucide;
  return candidate && typeof candidate === 'object' ? candidate as LucideRuntime : null;
}

function loadLucideRuntime(): Promise<LucideRuntime | null> {
  const existing = getLucideRuntime();
  if (existing) return Promise.resolve(existing);
  if (!lucideRuntimePromise) {
    lucideRuntimePromise = new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/lucide@latest/dist/umd/lucide.min.js';
      script.async = true;
      script.onload = () => resolve(getLucideRuntime());
      script.onerror = () => resolve(null);
      document.head.appendChild(script);
    });
  }
  return lucideRuntimePromise;
}

/**
 * Parses the portable QDS icon values used by qds-icon-picker.
 * Legacy `pi-*` values remain valid PrimeIcons inputs.
 */
export function parseQdsIcon(value: unknown): QdsIconReference | null {
  if (typeof value !== 'string') return null;
  const normalized = value.trim();
  if (normalized.startsWith('prime:')) {
    const name = normalized.slice('prime:'.length);
    return /^pi-[a-z0-9-]+$/i.test(name) ? { library: 'prime', name } : null;
  }
  if (normalized.startsWith('lucide:')) {
    const name = normalized.slice('lucide:'.length);
    return /^[a-z0-9-]+$/i.test(name) ? { library: 'lucide', name } : null;
  }
  return /^pi-[a-z0-9-]+$/i.test(normalized) ? { library: 'prime', name: normalized } : null;
}

/** Returns safe icon markup for a named or legacy PrimeIcons value. */
export function renderQdsIcon(
  value: unknown,
  className = '',
  part?: string
): string {
  const icon = parseQdsIcon(value);
  if (!icon) return '';
  const safeClassName = escapeHtml(className);
  const partAttribute = part ? ` part="${escapeHtml(part)}"` : '';
  if (icon.library === 'prime') {
    return `<i class="pi ${escapeHtml(icon.name)} ${safeClassName}"${partAttribute} aria-hidden="true"></i>`;
  }
  return `<i class="${safeClassName}" data-lucide="${escapeHtml(icon.name)}"${partAttribute} aria-hidden="true"></i>`;
}

/** Hydrates every safe Lucide marker in one shadow root. */
export function hydrateQdsLucideIcons(shadowRoot: ShadowRoot): void {
  const markers = [...shadowRoot.querySelectorAll<HTMLElement>('[data-lucide]')];
  if (!markers.length) return;

  void loadLucideRuntime().then((runtime) => {
    if (!runtime?.createElement) return;
    for (const marker of markers) {
      const name = marker.dataset.lucide;
      if (!name || !marker.isConnected) continue;
      const entry = Object.entries(runtime).find(([key]) => toKebabCase(key) === name);
      if (!entry) continue;
      const svg = runtime.createElement(entry[1]);
      svg.setAttribute('class', marker.className);
      const part = marker.getAttribute('part');
      if (part) svg.setAttribute('part', part);
      svg.setAttribute('aria-hidden', 'true');
      marker.replaceWith(svg);
    }
  });
}
